// Scrollspy for the desktop sidebar copy of the gs/toc card (.side-card--toc).
// Highlights the leaf whose target was most recently scrolled past, opens the
// <details> groups around it, and closes groups again once the reader leaves
// them — but only groups this script opened itself, never one the reader or
// the template's default-open rule (toc.html) opened. The inline mobile copy
// scrolls away with the article, so it's left alone.

// A target counts as "reached" once its top passes this fraction of the viewport.
const ACTIVATION_RATIO = 0.35;
const THROTTLE_MS = 80;

interface Leaf {
    link: HTMLAnchorElement;
    target: HTMLElement;
}

class TocScrollspy {
    private _card: HTMLElement | null = null;
    private _scroller: HTMLElement | null = null;
    private _leaves: Leaf[] = [];
    private _active: Leaf | null = null;
    private _autoOpened = new Set<HTMLDetailsElement>();
    private _ticking = false;

    init() {
        this._card = document.querySelector<HTMLElement>(".side-card--toc");

        if (!this._card) {
            return;
        }

        this._scroller = this._card.querySelector<HTMLElement>(".table-of-contents > .toc-scroll");

        this._card.querySelectorAll<HTMLAnchorElement>("a.toc-leaf").forEach((link) => {
            // Leaf ids are raw caption strings with spaces ("Tsuke-Men
            // Dipping Broth"); Hugo percent-encodes them in the href.
            const id = this.decode((link.getAttribute("href") || "").slice(1));
            const target = id ? document.getElementById(id) : null;

            if (target) {
                this._leaves.push({ link, target });
            }
        });

        if (this._leaves.length === 0) {
            return;
        }

        // A group the reader toggles is theirs from then on.
        this._card.querySelectorAll<HTMLDetailsElement>("details.toc-group").forEach((group) => {
            group.querySelector("summary")?.addEventListener("click", () => this._autoOpened.delete(group));
        });

        window.addEventListener("scroll", () => this.requestUpdate(), { passive: true });
        window.addEventListener("resize", () => this.requestUpdate(), { passive: true });
        this.update();
    }

    decode(id: string) {
        try {
            return decodeURIComponent(id);
        } catch {
            return id;
        }
    }

    requestUpdate() {
        if (this._ticking) {
            return;
        }

        // Timer, not requestAnimationFrame: a throttle this coarse is
        // invisible, and rAF never fires in a backgrounded/hidden view.
        this._ticking = true;
        window.setTimeout(() => {
            this._ticking = false;
            this.update();
        }, THROTTLE_MS);
    }

    update() {
        // Sidebar card is display:none below 860px — nothing to follow.
        if (!this._card || this._card.offsetParent === null) {
            return;
        }

        const line = window.innerHeight * ACTIVATION_RATIO;
        let current: Leaf | null = null;

        // Leaves are in document order, as are their targets.
        for (const leaf of this._leaves) {
            if (leaf.target.getBoundingClientRect().top <= line) {
                current = leaf;
            } else {
                break;
            }
        }

        if (current === this._active) {
            return;
        }

        this._active?.link.classList.remove("is-active");
        this._active = current;

        const keepOpen = new Set<HTMLDetailsElement>();

        if (current) {
            current.link.classList.add("is-active");

            for (let group = current.link.closest<HTMLDetailsElement>("details.toc-group"); group; group = group.parentElement?.closest<HTMLDetailsElement>("details.toc-group") ?? null) {
                keepOpen.add(group);

                if (!group.open) {
                    group.open = true;
                    this._autoOpened.add(group);
                }
            }
        }

        this._autoOpened.forEach((group) => {
            if (!keepOpen.has(group)) {
                group.open = false;
                this._autoOpened.delete(group);
            }
        });

        if (current) {
            this.revealInScroller(current.link);
        }
    }

    // Keeps the active leaf visible inside the card's own capped scroll box.
    // Adjusts that box's scrollTop directly — scrollIntoView would also
    // scroll the page, fighting the reader's own scrolling.
    revealInScroller(link: HTMLElement) {
        const scroller = this._scroller;

        if (!scroller || scroller.scrollHeight <= scroller.clientHeight) {
            return;
        }

        const box = scroller.getBoundingClientRect();
        const rect = link.getBoundingClientRect();
        const pad = 24;

        if (rect.top < box.top + pad) {
            scroller.scrollTop -= box.top + pad - rect.top;
        } else if (rect.bottom > box.bottom - pad) {
            scroller.scrollTop += rect.bottom - (box.bottom - pad);
        }
    }
}

export default new TocScrollspy();
